import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { Clock, Loader2, Send } from "lucide-react";
import BiSelect from "@/components/BiSelect";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const COMMON_TZS = [
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Perth",
  "Australia/Adelaide",
  "Australia/Hobart",
  "Australia/Darwin",
];

/**
 * DigestScheduleToggle — opt-in "Send my digest at 6pm" tile inside Settings.
 * Wired to GET/PUT /api/settings/digest. When switched ON the backend
 * scheduler (60s tick) fires ONE cost-alert push per local day at
 * `hour:00` in the tradie's chosen timezone.
 *
 * When OFF, the toggle collapses to a single row so it never crowds the
 * push-notifications section.
 */
export default function DigestScheduleToggle() {
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [state, setState] = useState({
    auto_send: false,
    hour: 18,
    timezone: "Australia/Sydney",
    last_sent_date: null,
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/digest");
        if (!cancel) setState(data);
      } catch (err) {
        devLog.warn("digest settings load failed", err);
      } finally {
        if (!cancel) setLoaded(true);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const sendNow = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post("/alerts/digest/notify");
      if (data?.sent > 0) {
        toast.success(
          data.count > 0
            ? `Sent · ${data.count} overrun${data.count === 1 ? "" : "s"} in tonight's digest`
            : "Sent · all jobs on budget today ✓"
        );
      } else {
        toast(
          "Digest ready — no push devices registered yet. Enable push notifications above to receive it.",
          { duration: 4500 }
        );
      }
    } catch (err) {
      devLog.warn("digest send-now failed", err);
      toast.error(err.response?.data?.detail || "Could not send digest");
    } finally {
      setTestBusy(false);
    }
  };

  const save = async (patch) => {
    const next = { ...state, ...patch };
    setState(next);
    setBusy(true);
    try {
      await api.put("/settings/digest", {
        auto_send: !!next.auto_send,
        hour: Number(next.hour),
        timezone: next.timezone,
      });
      if (patch.auto_send === true) {
        toast.success(`Evening digest scheduled for ${formatHour(next.hour)} ${next.timezone.split("/")[1] || ""}`);
      } else if (patch.auto_send === false) {
        toast("Evening digest paused");
      }
    } catch (err) {
      devLog.warn("digest settings save failed", err);
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;

  return (
    <section data-testid="digest-schedule-tile" className="mt-1 rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#FF9500]/12 text-[#FF9500] flex items-center justify-center shrink-0">
          <Clock className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-heading font-extrabold text-white leading-tight">
            Auto-send evening digest
          </div>
          <div className="text-[11px] text-white/50 leading-snug mt-0.5">
            One push per day summarising today&apos;s cost-code overruns.
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!state.auto_send}
            disabled={busy}
            onChange={(e) => save({ auto_send: e.target.checked })}
            data-testid="digest-auto-send-toggle"
            className="sr-only"
          />
          <span
            aria-hidden
            className={`relative w-11 h-6 rounded-full transition-colors ${
              state.auto_send ? "bg-[#FF9500]" : "bg-white/15"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                state.auto_send ? "translate-x-5" : ""
              }`}
            />
          </span>
        </label>
      </div>

      {state.auto_send && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">Send at</label>
            <BiSelect
              value={state.hour}
              disabled={busy}
              onChange={(e) => save({ hour: Number(e.target.value) })}
              data-testid="digest-hour-select"
              className="w-full bi-input h-10 text-sm"
            >
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {formatHour(h)}
                </option>
              ))}
            </BiSelect>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">Timezone</label>
            <BiSelect
              value={state.timezone}
              disabled={busy}
              onChange={(e) => save({ timezone: e.target.value })}
              data-testid="digest-timezone-select"
              className="w-full bi-input h-10 text-sm"
            >
              {COMMON_TZS.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.split("/")[1] || tz}
                </option>
              ))}
            </BiSelect>
          </div>
          {state.last_sent_date && (
            <div className="col-span-2 text-[11px] text-white/40 inline-flex items-center gap-1.5">
              {busy && <Loader2 className="w-3 h-3 animate-spin" />}
              Last sent · {state.last_sent_date}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={sendNow}
        disabled={testBusy}
        data-testid="digest-send-now-btn"
        className="mt-3 w-full h-10 rounded-lg bi-ghost-btn text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
      >
        {testBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        Send digest now (preview)
      </button>
    </section>
  );
}

function formatHour(h) {
  const suffix = h >= 12 ? "pm" : "am";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:00 ${suffix}`;
}
